/**
 * WaypointTrail — a trail-style progress indicator.
 * Replaces linear progress bars for multi-step flows (Cycle Readiness, Catchball lifecycle, etc.)
 *
 * Usage:
 *   <WaypointTrail waypoints={[
 *     { label: 'Pool Locked', status: 'complete' },
 *     { label: 'Sessions', status: 'active' },
 *     { label: 'Catchball', status: 'pending' },
 *     { label: 'Published', status: 'pending' },
 *   ]} />
 */

export type WaypointStatus = 'complete' | 'active' | 'pending';

export interface Waypoint {
  label: string;
  status: WaypointStatus;
  tooltip?: string;
}

interface WaypointTrailProps {
  waypoints: Waypoint[];
  /** Orientation — default horizontal */
  orientation?: 'horizontal' | 'vertical';
  className?: string;
}

export function WaypointTrail({
  waypoints,
  orientation = 'horizontal',
  className = '',
}: WaypointTrailProps) {
  if (orientation === 'vertical') {
    return <VerticalTrail waypoints={waypoints} className={className} />;
  }
  return <HorizontalTrail waypoints={waypoints} className={className} />;
}

function HorizontalTrail({ waypoints, className }: { waypoints: Waypoint[]; className: string }) {
  return (
    <div className={`flex items-start ${className}`} role="list" aria-label="Progress trail">
      {waypoints.map((wp, i) => (
        <div key={wp.label} className="flex items-start" role="listitem">
          {/* Waypoint column */}
          <div className="flex flex-col items-center gap-1.5">
            {/* Marker */}
            <div
              className={`relative flex items-center justify-center w-5 h-5 rounded-full border-2 transition-all duration-300 ${
                wp.status === 'complete'
                  ? 'bg-primary-600 border-primary-600'
                  : wp.status === 'active'
                  ? 'bg-[var(--bg-raised)] border-primary-500 shadow-sm shadow-primary-100'
                  : 'bg-[var(--bg-raised)] border-[var(--rule)]'
              }`}
              title={wp.tooltip}
            >
              {wp.status === 'active' && (
                <span className="absolute inset-0 rounded-full bg-primary-300 opacity-40 animate-ping" />
              )}
              {wp.status === 'complete' ? (
                <svg viewBox="0 0 10 10" className="w-2.5 h-2.5" fill="none" aria-hidden="true">
                  <path d="M2 5.5l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : wp.status === 'active' ? (
                <span className="w-2 h-2 rounded-full bg-primary-500" />
              ) : (
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--text-ter)]" />
              )}
            </div>
            {/* Label */}
            <span
              className={`text-[10px] leading-tight text-center whitespace-nowrap select-none ${
                wp.status === 'complete'
                  ? 'text-primary-600 font-medium'
                  : wp.status === 'active'
                  ? 'text-primary-700 font-semibold'
                  : 'text-[var(--text-ter)]'
              }`}
            >
              {wp.label}
            </span>
          </div>

          {/* Trail connector */}
          {i < waypoints.length - 1 && (
            <div className="flex flex-col items-center mt-2.5 mx-1">
              <div
                className={`h-0.5 w-8 sm:w-12 rounded-full transition-all duration-500 ${
                  wp.status === 'complete' ? 'bg-primary-400' : 'bg-[var(--hover-tint)]'
                } ${wp.status === 'complete' ? '' : 'border-dashed'}`}
                style={
                  wp.status !== 'complete'
                    ? { backgroundImage: 'repeating-linear-gradient(90deg, #d1d5db 0, #d1d5db 4px, transparent 4px, transparent 8px)' }
                    : {}
                }
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function VerticalTrail({ waypoints, className }: { waypoints: Waypoint[]; className: string }) {
  return (
    <div className={`flex flex-col ${className}`} role="list" aria-label="Progress trail">
      {waypoints.map((wp, i) => (
        <div key={wp.label} className="flex items-start gap-3" role="listitem">
          {/* Left column: marker + connector */}
          <div className="flex flex-col items-center flex-shrink-0">
            <div
              className={`relative flex items-center justify-center w-5 h-5 rounded-full border-2 transition-all duration-300 ${
                wp.status === 'complete'
                  ? 'bg-primary-600 border-primary-600'
                  : wp.status === 'active'
                  ? 'bg-[var(--bg-raised)] border-primary-500 shadow-sm shadow-primary-100'
                  : 'bg-[var(--bg-raised)] border-[var(--rule)]'
              }`}
              title={wp.tooltip}
            >
              {wp.status === 'active' && (
                <span className="absolute inset-0 rounded-full bg-primary-300 opacity-40 animate-ping" />
              )}
              {wp.status === 'complete' ? (
                <svg viewBox="0 0 10 10" className="w-2.5 h-2.5" fill="none" aria-hidden="true">
                  <path d="M2 5.5l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : wp.status === 'active' ? (
                <span className="w-2 h-2 rounded-full bg-primary-500" />
              ) : (
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--text-ter)]" />
              )}
            </div>
            {i < waypoints.length - 1 && (
              <div
                className={`w-0.5 h-6 my-0.5 rounded-full ${
                  wp.status === 'complete' ? 'bg-primary-300' : 'bg-[var(--hover-tint)]'
                }`}
              />
            )}
          </div>
          {/* Right: label */}
          <span
            className={`text-sm pt-0.5 pb-6 last:pb-0 ${
              wp.status === 'complete'
                ? 'text-primary-600 font-medium'
                : wp.status === 'active'
                ? 'text-[var(--text-pri)] font-medium'
                : 'text-[var(--text-ter)]'
            }`}
          >
            {wp.label}
          </span>
        </div>
      ))}
    </div>
  );
}
